// Homepage alert indicator for the staff Enquiries tracker. Shows an icon + count badge when
// there are open enquiries (or any assigned to the caller). Neutral normally; attention (red)
// when the caller has overdue enquiries (falls back to any overdue). Click → /enquiries,
// pre-filtered to "assigned to me" when the caller has open assignments. Staff-only: gated on
// the shared canAccessRoute('/enquiries') allow-list, so students/clients never see it.

import { useEffect, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { canAccessRoute } from '@avsuite/shared';
import { useAuth } from '../context/AuthContext';
import { fetchSummary, type EnquirySummary } from '../lib/enquiries';

export default function EnquiriesHomeBadge() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<EnquirySummary | null>(null);

  const canSee = canAccessRoute('/enquiries', user?.roles ?? []);

  useEffect(() => {
    if (!canSee) return;
    let alive = true;
    fetchSummary().then((d) => { if (alive) setSummary(d); });
    return () => { alive = false; };
  }, [canSee]);

  if (!canSee || !summary) return null;

  // Alert whenever there is anything open OR anything assigned to the caller (regardless of overdue).
  const show = summary.open_total > 0 || summary.assigned_to_me_open > 0;
  if (!show) return null;

  // Attention colour when the caller has overdue items; fall back to any overdue in the tenant.
  const attention = summary.overdue_mine > 0 || summary.overdue_total > 0;
  const noun = summary.open_total === 1 ? 'open enquiry' : 'open enquiries';
  const overduePart = attention
    ? ` · ${summary.overdue_mine > 0 ? `${summary.overdue_mine} overdue for you` : `${summary.overdue_total} overdue`}`
    : '';
  const assignedPart = summary.assigned_to_me_open > 0 ? ` · ${summary.assigned_to_me_open} assigned to you` : '';
  const label = `${summary.open_total} ${noun}${assignedPart}${overduePart}`;
  const target = summary.assigned_to_me_open > 0 ? '/enquiries?assignee=me' : '/enquiries';

  const card: CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 12, width: '100%',
    padding: '14px 18px', marginTop: 16,
    background: '#1e293b', border: `1px solid ${attention ? '#7f1d1d' : '#334155'}`,
    borderRadius: 8, cursor: 'pointer', textAlign: 'left',
    color: '#e2e8f0', fontFamily: 'inherit', fontSize: 14,
  };
  const countBadge: CSSProperties = {
    minWidth: 22, height: 22, padding: '0 6px', borderRadius: 11,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12, fontWeight: 700,
    background: attention ? '#dc2626' : '#334155', color: '#fff',
  };

  return (
    <button type="button" onClick={() => navigate(target)} title={label} aria-label={label} style={card}>
      <span aria-hidden="true" style={{ fontSize: 18 }}>📨</span>
      <span style={{ fontWeight: 600 }}>Enquiries</span>
      <span style={countBadge}>{summary.open_total > 99 ? '99+' : summary.open_total}</span>
      <span style={{ color: '#94a3b8', fontSize: 13 }}>
        {assignedPart ? `${summary.assigned_to_me_open} assigned to you` : 'open'}
        {attention && <span style={{ color: '#fca5a5', fontWeight: 600 }}>{summary.overdue_mine > 0 ? ` · ${summary.overdue_mine} overdue` : ` · ${summary.overdue_total} overdue`}</span>}
      </span>
      <span style={{ marginLeft: 'auto', color: '#7dd3fc', fontSize: 13 }}>→</span>
    </button>
  );
}
